import { useAuth } from '@clerk/react'
import type {
  Layout as FlexLayoutInstance,
  IJsonModel,
  ITabRenderValues,
  Model,
  TabNode,
} from 'flexlayout-react'
import {
  type Action,
  Actions,
  DockLocation,
  Model as FlexLayoutModel,
  I18nLabel,
  Layout,
} from 'flexlayout-react'
import {
  BookOpen,
  CheckCircle,
  CheckSquare,
  Code as CodeIcon,
  FileText,
  RotateCcw,
  Settings,
  StickyNote,
  Terminal,
} from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from '#router'
import { usePageTitle } from '@/hooks/use-document-title'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  getPresetLayout,
  getStoredActiveLayoutId,
  getStoredLayout,
  normalizeAuthTabs,
  storeActiveLayoutId,
  storeLayout,
} from '@/lib/layout-presets'
import { selectHighestPriorityTabs } from '@/lib/tab-priorities'
import { trpc } from '@/lib/trpc'
import { useIsAdmin } from './admin-protected-route'
import { AttemptsPane, useSessionRefresh } from './editor/attempts-pane'
import { CodeEditorPane } from './editor/code-pane'
import { DescriptionPane } from './editor/description-pane'
import { EditorContextProvider } from './editor/editor-context'
import { EditorialPane } from './editor/editorial-pane'
import { useExecutionOptional } from './editor/execution-context'
import { useSubmissionSuccess } from './editor/hooks/use-submission-success'
import { useTabSwitcher } from './editor/hooks/use-tab-switcher'
import { MobileEditor } from './editor/mobile-editor'
import { NotesPane } from './editor/notes-pane'
import { OutputPane } from './editor/output-pane'
import { useProblemOptional } from './editor/problem-context'
import { SessionLifecycleModals } from './editor/session-lifecycle-modals'
import { EditorSettingsModalProvider } from './editor/settings-modal/editor-settings-modal-context'
import { TestCaseInjectionProvider } from './editor/test-case-injection-context'
import { TestCasesPane } from './editor/test-cases-pane'
import { TestResultsPane } from './editor/test-results-pane'
import { TimerSubmissionWatcher } from './editor/timer/timer-submission-watcher'
import { useStreak } from './streak-context'
import { useUserSettings } from './user-settings-context'
import '@/styles/editor.css'
import '@/styles/md-editor.css'
import 'flexlayout-react/style/light.css'

type TabIconComponent = React.ComponentType<{
  className?: string
  size?: number
  color?: string
}>

const TAB_DEFINITIONS = [
  {
    id: 'description-tab',
    name: 'Description',
    component: 'description',
    paneLabel: 'Description Pane',
    icon: FileText,
    iconColor: '#3b82f6',
    group: 'left',
  },
  {
    id: 'editorial-tab',
    name: 'Editorial',
    component: 'editorial',
    paneLabel: 'Editorial Pane',
    icon: BookOpen,
    iconColor: '#f97316',
    group: 'left',
  },
  {
    id: 'attempts-tab',
    name: 'Attempts',
    component: 'attempts',
    paneLabel: 'Attempts Pane',
    icon: RotateCcw,
    iconColor: '#60a5fa',
    group: 'left',
  },
  {
    id: 'notes-tab',
    name: 'Note',
    component: 'notes',
    paneLabel: 'Notes Pane',
    icon: StickyNote,
    iconColor: '#eab308',
    group: 'left',
  },
  {
    id: 'code-editor-tab',
    name: 'Code',
    component: 'code',
    paneLabel: 'Code Editor Pane',
    icon: CodeIcon,
    iconColor: '#22c55e',
    group: 'code',
  },
  {
    id: 'test-cases-tab',
    name: 'Test Cases',
    component: 'test-cases',
    paneLabel: 'Test Cases Pane',
    icon: CheckSquare,
    iconColor: '#22c55e',
    group: 'tests',
  },
  {
    id: 'test-results-tab',
    name: 'Test Results',
    component: 'test-results',
    paneLabel: 'Test Results Pane',
    icon: CheckCircle,
    iconColor: '#22c55e',
    group: 'tests',
  },
  {
    id: 'output-tab',
    name: 'Output',
    component: 'output',
    paneLabel: 'Output Pane',
    icon: Terminal,
    iconColor: '#22c55e',
    group: 'output',
  },
] as const

type TabDefinition = (typeof TAB_DEFINITIONS)[number]
type TabComponent = TabDefinition['component']

const TAB_DEFINITION_BY_ID: Record<string, TabDefinition> =
  TAB_DEFINITIONS.reduce<Record<string, TabDefinition>>((acc, tab) => {
    acc[tab.id] = tab
    return acc
  }, {})

const TAB_DEFINITION_BY_COMPONENT: Record<TabComponent, TabDefinition> =
  TAB_DEFINITIONS.reduce<Record<TabComponent, TabDefinition>>(
    (acc, tab) => {
      acc[tab.component as TabComponent] = tab
      return acc
    },
    {} as Record<TabComponent, TabDefinition>,
  )

/**
 * Maps URL `tab` query parameter values to FlexLayout tab IDs.
 * Only Editorial, Attempts, and Notes are URL-linkable. Description is the
 * priority-1 default and is represented by absence of the `?tab=` param
 * (so `?tab=description` is treated as no param). Right-pane tabs (code,
 * test cases, test results) aren't meaningful to share externally.
 * Derived from TAB_DEFINITIONS so the two stay in sync automatically.
 */
export const TAB_PARAM_TO_ID: Record<string, string | undefined> =
  Object.fromEntries(
    TAB_DEFINITIONS.filter(
      (t) => t.group === 'left' && t.component !== 'description',
    ).map((t) => [t.component, t.id]),
  )

/** Reverse mapping: FlexLayout tab IDs back to URL param values. */
export const TAB_ID_TO_PARAM: Record<string, string | undefined> =
  Object.fromEntries(
    TAB_DEFINITIONS.filter(
      (t) => t.group === 'left' && t.component !== 'description',
    ).map((t) => [t.id, t.component]),
  )

// Component to handle auto-switching to the appropriate results tab when execution starts
function AutoSwitchToResults({ model }: { model: Model | null }) {
  const executionContext = useExecutionOptional()
  const prevIsExecuting = useRef(false)

  useEffect(() => {
    if (model && !prevIsExecuting.current && executionContext?.isExecuting) {
      // Switch to Output tab for raw execution, Test Results tab otherwise
      const targetTab =
        executionContext.executionMode === 'raw'
          ? 'output-tab'
          : 'test-results-tab'
      try {
        model.doAction(Actions.selectTab(targetTab))
      } catch {
        // Tab might not exist in current layout, ignore
      }
    }
    prevIsExecuting.current = executionContext?.isExecuting ?? false
  }, [model, executionContext?.isExecuting, executionContext?.executionMode])

  return null
}

/**
 * Wraps SessionLifecycleModals with desktop tab-switching behavior.
 */
function DesktopSessionLifecycleModals({ model }: { model: Model | null }) {
  const { switchToTab } = useTabSwitcher(model)
  return (
    <SessionLifecycleModals
      onSwitchToAttempts={() => switchToTab('attempts-tab')}
    />
  )
}

/**
 * Component that handles auto-switching to Attempts tab when submission succeeds.
 * Uses composition of focused hooks to separate concerns:
 * - useSubmissionSuccess: Detects successful submissions
 * - useTabSwitcher: Handles tab navigation
 * - useSessionRefresh: Refreshes session history data
 */
function AutoSwitchToAttempts({
  model,
  problemId,
  onAutoExpandExecutionId,
}: {
  model: Model | null
  problemId: string | null
  onAutoExpandExecutionId: (id: string | null) => void
}) {
  const executionContext = useExecutionOptional()
  const sessionRefresh = useSessionRefresh()
  const { switchToTab } = useTabSwitcher(model)
  const streakContext = useStreak()
  const streakCheck = trpc.streak.check.useMutation()
  const utils = trpc.useUtils()

  useSubmissionSuccess({
    executionResult: executionContext?.result ?? null,
    executionMode: executionContext?.executionMode ?? null,
    isExecuting: executionContext?.isExecuting ?? false,
    onSuccess: (executionId) => {
      if (!sessionRefresh) return

      // Refresh attempts data, then switch tabs
      sessionRefresh()
        .then(() => {
          switchToTab('attempts-tab')
          onAutoExpandExecutionId(executionId)
        })
        .catch((error) => {
          console.error('Failed to refresh attempts:', error)
        })

      // Check streak (fire-and-forget, doesn't block the UI)
      if (problemId) {
        streakCheck.mutate(
          { problemId },
          {
            onSuccess: (result) => {
              utils.streak.get.invalidate()
              if (result.earned) {
                streakContext.triggerStreakEarned(result.currentStreak)
              }
            },
          },
        )
      }
    },
  })

  return null
}

function slugToDisplayTitle(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function ProblemPageTitle() {
  const problem = useProblemOptional()
  const location = useLocation()
  const slug = location.searchParams.get('problem')
  // Use the slug-derived title until the MDX import + tRPC fetch resolve.
  // Without this fallback, the title flashes to the site default during the
  // load window — and stays there if the fetch ever stalls.
  const fallback = slug ? slugToDisplayTitle(slug) : null
  const resolvedTitle = problem?.data?.title ?? fallback
  usePageTitle(resolvedTitle)
  return null
}

export default function Editor() {
  const location = useLocation()
  const isMobile = useIsMobile()
  const [model, setModel] = useState<Model | null>(null)
  const [layoutKey, setLayoutKey] = useState(0)
  const layoutContainerRef = useRef<HTMLDivElement | null>(null)
  const layoutRef = useRef<FlexLayoutInstance | null>(null)
  const rawProblemSlug = location.searchParams.get('problem')
  const prevProblemSlugRef = useRef(rawProblemSlug)
  const tabParam = location.searchParams.get('tab')
  const { isSignedIn, isLoaded: isAuthLoaded, userId } = useAuth()
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const utils = trpc.useUtils()
  const [problemId, setProblemId] = useState<string | null>(null)

  const problemSlug = rawProblemSlug

  const { settings, settingsLoaded } = useUserSettings()
  const activeLayoutIdRef = useRef(settings.layouts.activeLayoutId)
  // Distinguishes the initial settings hydration from a user-driven layout
  // switch. Without this, the hydration that flips activeLayoutId from the
  // default placeholder to the user's real layout fires the layout-change
  // effect and re-creates the model from priority defaults — clobbering the
  // tab the model-init effect just selected from the URL `?tab=` param.
  const hasHydratedSettingsRef = useRef(false)

  // Platform detection for shortcut labels
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform)
  const maximizeShortcut = isMac ? '⌥ =' : 'Alt + ='

  // Customize FlexLayout's built-in button labels with platform-specific shortcuts
  const i18nMapper = useCallback(
    (id: I18nLabel) => {
      if (id === I18nLabel.Maximize)
        return `Maximize this view (${maximizeShortcut})`
      if (id === I18nLabel.Restore) return `Restore view (${maximizeShortcut})`
      return undefined
    },
    [maximizeShortcut],
  )

  // State for auto-switching to Attempts tab on successful submission
  const [autoExpandExecutionId, setAutoExpandExecutionId] = useState<
    string | null
  >(null)

  // Resolve which IJsonModel to use.
  // Selects the highest-priority tab in each tabset for a fresh model load;
  // later logic (e.g. a valid `?tab=` URL parameter) can still override the
  // active tab.
  const resolveLayout = useCallback(
    (signedIn: boolean): IJsonModel => {
      // 1. Try localStorage (only if we have a userId and the stored layout
      //    matches the current activeLayoutId — otherwise it's stale from
      //    another session)
      if (userId) {
        const stored = getStoredLayout(userId)
        if (stored) {
          const storedActiveId = getStoredActiveLayoutId(userId)
          if (storedActiveId === settings.layouts.activeLayoutId) {
            return selectHighestPriorityTabs(
              normalizeAuthTabs(stored, signedIn),
            )
          }
        }
      }

      // 2. Try activeLayoutId from settings
      const activeId = settings.layouts.activeLayoutId
      const preset = getPresetLayout(activeId, signedIn)
      if (preset) return selectHighestPriorityTabs(preset)

      // 3. Try custom layout
      const custom = settings.layouts.custom.find((c) => c.id === activeId)
      if (custom) {
        return selectHighestPriorityTabs(
          normalizeAuthTabs(custom.model as unknown as IJsonModel, signedIn),
        )
      }

      // 4. Fall back to default
      return selectHighestPriorityTabs(getPresetLayout('default', signedIn)!)
    },
    [userId, settings.layouts],
  )

  useEffect(() => {
    // Wait for both auth and user settings before initializing the model.
    // Without `settingsLoaded`, the first paint uses DEFAULT_USER_SETTINGS
    // (activeLayoutId = 'default'), then the layout-change effect immediately
    // swaps to the user's real layout once it hydrates from the DB — which
    // shows up as a visible flash on direct-link loads.
    if (
      model ||
      typeof window === 'undefined' ||
      !isAuthLoaded ||
      !settingsLoaded
    )
      return

    const signedIn = !!isSignedIn
    let newModel: Model | null = null

    // First attempt: use the resolved layout (may come from localStorage or custom settings)
    try {
      const layout = resolveLayout(signedIn)
      newModel = FlexLayoutModel.fromJson(layout)
    } catch (err) {
      console.error(
        'Failed to load editor layout, falling back to default:',
        err,
      )
      // Fallback: try a known-good default preset layout
      try {
        const fallbackLayout = getPresetLayout('default', signedIn)
        if (!fallbackLayout) {
          console.error('No default layout preset available')
          return
        }
        newModel = FlexLayoutModel.fromJson(fallbackLayout)
      } catch (fallbackErr) {
        console.error('Failed to load default layout preset:', fallbackErr)
        return
      }
    }

    if (!newModel) {
      return
    }

    // Sanitize conflicting or invalid URL params
    if (tabParam) {
      if (!TAB_PARAM_TO_ID[tabParam]) {
        // Invalid value — strip the tab param
        const url = new URL(window.location.href)
        url.searchParams.delete('tab')
        window.history.replaceState(window.history.state, '', url.toString())
      } else {
        try {
          newModel.doAction(Actions.selectTab(TAB_PARAM_TO_ID[tabParam]))
        } catch {
          // Tab doesn't exist in current layout, ignore
        }
      }
    }

    setModel(newModel)
  }, [model, tabParam, isSignedIn, isAuthLoaded, settingsLoaded, resolveLayout])

  useEffect(() => {
    if (!model || !isSignedIn) return

    const authTabs = TAB_DEFINITIONS.filter(
      (t) => t.component === 'attempts' || t.component === 'notes',
    )

    // Prefer LEFT_PANE, fall back to the first tabset in the model
    let targetId: string | undefined
    if (model.getNodeById('LEFT_PANE')) {
      targetId = 'LEFT_PANE'
    } else {
      model.visitNodes((node) => {
        if (!targetId && node.getType() === 'tabset') {
          targetId = node.getId()
        }
      })
    }
    if (!targetId) return

    for (const tab of authTabs) {
      if (model.getNodeById(tab.id)) continue
      try {
        model.doAction(
          Actions.addNode(
            {
              type: 'tab',
              id: tab.id,
              name: tab.name,
              component: tab.component,
              enableClose: false,
            },
            targetId,
            DockLocation.CENTER,
            -1,
            false,
          ),
        )
      } catch {
        // Layout may not support this tab placement — skip gracefully
      }
    }
  }, [model, isSignedIn])

  // React to layout changes from settings (Apply button or cross-session DB sync)
  useEffect(() => {
    if (!settingsLoaded) return

    // First time settings hydrate: sync the ref so the model-init effect's
    // result (which honors any URL `?tab=` param) isn't overwritten here.
    if (!hasHydratedSettingsRef.current) {
      hasHydratedSettingsRef.current = true
      activeLayoutIdRef.current = settings.layouts.activeLayoutId
      return
    }

    if (settings.layouts.activeLayoutId === activeLayoutIdRef.current) return
    activeLayoutIdRef.current = settings.layouts.activeLayoutId

    if (!userId) return

    const activeId = settings.layouts.activeLayoutId
    const signedIn = !!isSignedIn

    try {
      // Resolve from the activeLayoutId directly, not localStorage — localStorage
      // may be stale if the layout was changed in another session/browser.
      let layoutJson: IJsonModel | null =
        getPresetLayout(activeId, signedIn) ?? null

      if (!layoutJson) {
        const custom = settings.layouts.custom.find((c) => c.id === activeId)
        if (custom) {
          layoutJson = normalizeAuthTabs(
            custom.model as unknown as IJsonModel,
            signedIn,
          )
        }
      }

      if (!layoutJson) {
        layoutJson = getPresetLayout('default', signedIn)!
      }

      // Activate the highest-priority tab in each tabset when applying a new layout
      layoutJson = selectHighestPriorityTabs(layoutJson)

      // Sync localStorage so subsequent mounts use the correct layout
      storeLayout(userId, layoutJson)
      storeActiveLayoutId(userId, activeId)

      const newModel = FlexLayoutModel.fromJson(layoutJson)
      setModel(newModel)
      setLayoutKey((k) => k + 1)
    } catch (err) {
      console.error('Failed to apply layout:', err)
    }
  }, [settings.layouts, userId, isSignedIn, settingsLoaded])

  useEffect(() => {
    const root = layoutContainerRef.current
    if (!root) return

    const handleWheel = (event: WheelEvent) => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      const tabStrip = target.closest<HTMLElement>(
        '.flexlayout__tabset_tabbar_inner',
      )
      if (!tabStrip) return

      const absX = Math.abs(event.deltaX)
      const absY = Math.abs(event.deltaY)
      const horizontalIntent = absX >= absY && absX > 0.5
      const shiftScroll = event.shiftKey && absY > 0.5

      if (!horizontalIntent && !shiftScroll) return

      const delta = horizontalIntent ? event.deltaX : event.deltaY
      if (delta === 0) return

      const previousScroll = tabStrip.scrollLeft
      tabStrip.scrollLeft += delta

      if (tabStrip.scrollLeft !== previousScroll) {
        event.preventDefault()
      }
    }

    root.addEventListener('wheel', handleWheel, { passive: false })
    return () => root.removeEventListener('wheel', handleWheel)
  }, [])

  // FlexLayout maximizes a tabset when its tab strip is double-clicked, which
  // fires on accidental rapid clicks. Swallow the event before it reaches
  // React's delegated handler — the header maximize button and Alt+= still
  // work, since they don't go through dblclick.
  useEffect(() => {
    const root = layoutContainerRef.current
    if (!root) return

    const handleDoubleClick = (event: MouseEvent) => {
      const target = event.target
      // Element, not HTMLElement — tab icons are SVG elements
      if (!(target instanceof Element)) return
      if (target.closest('[data-layout-path$="/tabstrip"]')) {
        event.stopPropagation()
      }
    }

    root.addEventListener('dblclick', handleDoubleClick, true)
    return () => root.removeEventListener('dblclick', handleDoubleClick, true)
  }, [])

  // Alt++ to maximize/restore the active panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.code === 'Equal' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        const activeTabset = model?.getActiveTabset()
        if (activeTabset) {
          model!.doAction(Actions.maximizeToggle(activeTabset.getId()))
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [model])

  // Replace native title tooltips on FlexLayout toolbar buttons with a single
  // fixed-position tooltip element on document.body. This bypasses all ancestor
  // overflow clipping that breaks CSS pseudo-element tooltips.
  useEffect(() => {
    const container = layoutContainerRef.current
    if (!container) return

    // Create shared tooltip element once
    let tooltip = document.getElementById(
      'editor-fixed-tooltip',
    ) as HTMLDivElement | null
    if (!tooltip) {
      tooltip = document.createElement('div')
      tooltip.id = 'editor-fixed-tooltip'
      tooltip.className = 'editor-fixed-tooltip'
      document.body.appendChild(tooltip)
    }
    const tip = tooltip

    function showTooltip(e: Event) {
      const btn = e.currentTarget as HTMLElement
      const text = btn.getAttribute('data-tooltip')
      if (!text || !tip) return
      tip.textContent = text
      // Position below the button, centered — but clamp to viewport
      const rect = btn.getBoundingClientRect()
      tip.style.top = `${rect.bottom + 8}px`
      tip.style.opacity = '1'

      // Measure tooltip width to detect right-edge clipping
      tip.style.left = '0'
      tip.style.transform = 'none'
      const tipWidth = tip.offsetWidth
      const centerX = rect.left + rect.width / 2
      const rightEdge = centerX + tipWidth / 2
      const viewportWidth = window.innerWidth

      if (rightEdge > viewportWidth - 8) {
        // Anchor to right edge with padding
        tip.style.left = `${viewportWidth - tipWidth - 8}px`
        tip.style.transform = 'none'
      } else {
        tip.style.left = `${centerX}px`
        tip.style.transform = 'translateX(-50%)'
      }
    }

    function hideTooltip() {
      if (tip) tip.style.opacity = '0'
    }

    function setupButtons(root: ParentNode) {
      // Only target buttons that still have a native title (not yet processed)
      const buttons = root.querySelectorAll(
        '.flexlayout__tab_toolbar_button[title]',
      )
      for (let i = 0; i < buttons.length; i++) {
        const btn = buttons[i] as HTMLElement
        const title = btn.getAttribute('title')
        if (title) {
          btn.setAttribute('data-tooltip', title)
          btn.removeAttribute('title')
          btn.addEventListener('mouseenter', showTooltip)
          btn.addEventListener('mouseleave', hideTooltip)
        }
      }
    }

    setupButtons(container)

    const observer = new MutationObserver(() => setupButtons(container))
    observer.observe(container, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      // Clean up listeners from all buttons
      const buttons = container.querySelectorAll(
        '.flexlayout__tab_toolbar_button[data-tooltip]',
      )
      for (let i = 0; i < buttons.length; i++) {
        buttons[i].removeEventListener('mouseenter', showTooltip)
        buttons[i].removeEventListener('mouseleave', hideTooltip)
      }
      if (tip) tip.style.opacity = '0'
    }
  }, [model])

  // Fetch problem ID (used for admin quick-jump)
  useEffect(() => {
    // Reset problemId when problemSlug changes to ensure fresh fetch
    setProblemId(null)

    if (!problemSlug) return

    // Guard against a slower response for a previous slug resolving after a
    // newer one and overwriting problemId with the wrong problem's id.
    let ignore = false

    const fetchProblemId = async () => {
      try {
        const data = await utils.problems.resolveSlug.fetch({
          slug: problemSlug,
        })
        if (!ignore) setProblemId(data.problemId)
      } catch (error) {
        if (!ignore) console.error('Failed to fetch problem ID:', error)
      }
    }

    fetchProblemId()

    return () => {
      ignore = true
    }
  }, [problemSlug, utils])

  // Activate each tabset's highest-priority tab when navigating to a new
  // problem. Preserves the layout structure (pane arrangement) but ensures
  // a deterministic, ranked tab is active so the previously-selected tab
  // (e.g. Editorial) doesn't leak across problems.
  useEffect(() => {
    if (!model) return
    if (prevProblemSlugRef.current === problemSlug) return
    prevProblemSlugRef.current = problemSlug

    try {
      // Serialize current model → reapply priority selection → rebuild
      const json = selectHighestPriorityTabs(model.toJson())
      const newModel = FlexLayoutModel.fromJson(json)
      setModel(newModel)

      // Persist so next page load uses the same priority-resolved selection
      if (userId) {
        storeLayout(userId, json)
      }
    } catch (err) {
      console.error('Failed to apply tab priority on problem navigation:', err)
    }

    // Drop any `?tab=` deep-link param — it's specific to the prior problem.
    const url = new URL(window.location.href)
    if (url.searchParams.has('tab')) {
      url.searchParams.delete('tab')
      window.history.replaceState(window.history.state, '', url.toString())
    }
  }, [model, problemSlug, userId])

  const factory = useCallback(
    (node: TabNode) => {
      const component = node.getComponent() as TabComponent
      const tabMeta = TAB_DEFINITION_BY_COMPONENT[component]

      if (component === 'test-cases') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Test Cases Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <TestCasesPane problemSlug={problemSlug} />
          </section>
        )
      }

      if (component === 'test-results') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Test Results Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <TestResultsPane />
          </section>
        )
      }

      if (component === 'code') {
        return (
          <div className="h-full">
            <CodeEditorPane />
          </div>
        )
      }

      if (component === 'description') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Description Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <DescriptionPane />
          </section>
        )
      }

      if (component === 'editorial') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Editorial Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <EditorialPane />
          </section>
        )
      }

      if (component === 'attempts') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Attempts Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <AttemptsPane
              problemId={problemId}
              autoExpandExecutionId={autoExpandExecutionId}
              onSwitchToCode={() => {
                if (model) {
                  try {
                    model.doAction(Actions.selectTab('code-editor-tab'))
                  } catch {
                    // Tab might not exist; ignore.
                  }
                }
              }}
            />
          </section>
        )
      }

      if (component === 'notes') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Notes Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <NotesPane />
          </section>
        )
      }

      if (component === 'output') {
        return (
          <section
            aria-label={tabMeta?.paneLabel || 'Output Pane'}
            className="pane-card h-full"
            data-pane={component}
          >
            <OutputPane />
          </section>
        )
      }

      const Tag =
        tabMeta?.group === 'left' ? ('aside' as const) : ('section' as const)
      const label = tabMeta?.paneLabel || `${node.getName()} Pane`

      return (
        <Tag
          aria-label={label}
          role="region"
          className="pane-card"
          data-pane={component}
        >
          <p className="text-muted-foreground p-4 text-center">{label}</p>
        </Tag>
      )
    },
    [problemSlug, problemId, autoExpandExecutionId, model],
  )

  const onRenderTab = useCallback(
    (node: TabNode, renderValues: ITabRenderValues) => {
      const tabMeta = TAB_DEFINITION_BY_ID[node.getId()]
      if (tabMeta) {
        const Icon = tabMeta.icon as TabIconComponent
        renderValues.content = (
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4" color={tabMeta.iconColor} />
            {/* Definition name, not node.getName() — layouts persisted in
                localStorage keep the tab name they were saved with, so a
                rename (e.g. "AI Assistant" → "Assistant") would otherwise
                never reach existing users. */}
            <span>{tabMeta.name}</span>
          </span>
        )
      }

      // Admin quick-jump to the problem editor, shown as a trailing gear on
      // the Description tab. Rendered only for admins — non-admins get no
      // placeholder, no DOM node, nothing to detect. stopPropagation on
      // pointerdown/click keeps the gear from selecting or dragging the tab
      // (same pattern as FlexLayout's own close button).
      if (
        node.getId() === 'description-tab' &&
        isAdminLoaded &&
        isAdmin &&
        problemId
      ) {
        renderValues.buttons.push(
          <Link
            key="admin-edit-problem"
            href={`/admin/problems/${problemId}`}
            title="Edit problem in admin"
            aria-label="Edit problem in admin"
            draggable={false}
            className="ml-1 inline-flex items-center text-muted-foreground transition-colors hover:text-foreground"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Settings className="h-3.5 w-3.5" />
          </Link>,
        )
      }
    },
    [isAdmin, isAdminLoaded, problemId],
  )

  // Coalesce layout persistence so we only serialize & store at most once per frame.
  const layoutSaveScheduledRef = useRef(false)

  const onAction = useCallback(
    (action: Action) => {
      if (action.type === Actions.SELECT_TAB) {
        const tabId = action.data?.tabNode as string
        const url = new URL(window.location.href)

        if (tabId === 'description-tab') {
          // Description is the priority default — represented by the absence
          // of the `?tab=` param rather than `?tab=description`.
          url.searchParams.delete('tab')
          window.history.replaceState(window.history.state, '', url.toString())
        } else {
          const paramValue = TAB_ID_TO_PARAM[tabId]
          if (paramValue) {
            url.searchParams.set('tab', paramValue)
            window.history.replaceState(
              window.history.state,
              '',
              url.toString(),
            )
          }
        }
      }

      // Persist the updated model to localStorage after FlexLayout applies the action.
      // Use requestAnimationFrame and a ref-based flag to avoid redundant writes
      // during high-frequency actions like drag/resize.
      if (model && userId && !layoutSaveScheduledRef.current) {
        layoutSaveScheduledRef.current = true
        requestAnimationFrame(() => {
          layoutSaveScheduledRef.current = false
          try {
            storeLayout(userId, model.toJson())
          } catch {
            // Silently fail — layout persistence is best-effort
          }
        })
      }

      return action
    },
    [model, userId],
  )

  return (
    <EditorContextProvider problemSlug={problemSlug}>
      <TestCaseInjectionProvider>
        <EditorSettingsModalProvider>
          <ProblemPageTitle />
          {isMobile ? (
            <MobileEditor />
          ) : (
            <>
              <AutoSwitchToResults model={model} />
              <AutoSwitchToAttempts
                model={model}
                problemId={problemId}
                onAutoExpandExecutionId={setAutoExpandExecutionId}
              />
              <DesktopSessionLifecycleModals model={model} />
              <TimerSubmissionWatcher />
              <main className="editor-page" aria-label="Coding workspace">
                <div className="page-shell editor-stage">
                  <section
                    className="editor-layout-shell"
                    aria-label="Editor layout"
                  >
                    <div
                      className="editor-layout-container"
                      ref={layoutContainerRef}
                    >
                      {model ? (
                        <Layout
                          key={layoutKey}
                          ref={layoutRef}
                          model={model}
                          factory={factory}
                          onRenderTab={onRenderTab}
                          onAction={onAction}
                          i18nMapper={i18nMapper}
                          realtimeResize
                          icons={{
                            more: () => null,
                          }}
                        />
                      ) : (
                        <div className="pane-card" aria-hidden />
                      )}
                    </div>
                  </section>
                </div>
              </main>
            </>
          )}
        </EditorSettingsModalProvider>
      </TestCaseInjectionProvider>
    </EditorContextProvider>
  )
}
